import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProjects } from '@/context/ProjectContext';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/StatusBadge';
import { Plus, Search, Filter } from 'lucide-react';
import { formatCurrency, formatDate, getPaymentStatus, getPaymentTypeLabel } from '@/utils/helpers';

export const ProjectList = () => {
  const { projects } = useProjects();
  const { user, isAdmin } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Filter projects based on user role
  const userProjects = isAdmin
    ? projects
    : projects.filter((p) => p.createdBy === user?.id);

  // Apply filters
  const filteredProjects = userProjects.filter((project) => {
    const matchesSearch =
      project.projectName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.clientName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || project.paymentType === filterType;
    const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const getProjectStatus = (project) => {
    const hasOverdue = project.paymentSchedule.some(
      (p) => getPaymentStatus(p.dueDate, p.status) === 'overdue'
    );
    if (hasOverdue) return 'overdue';
    
    const allPaid = project.paymentSchedule.every((p) => p.status === 'paid');
    if (allPaid) return 'completed';
    
    return 'active';
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Projects</h1>
          <p className="text-muted-foreground mt-1">
            Manage and track all your projects
          </p>
        </div>
        <Link to="/projects/new">
          <Button size="lg" className="gap-2">
            <Plus className="h-4 w-4" />
            New Project
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search projects or clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full md:w-[200px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Payment Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="outsourced">Outsourced</SelectItem>
                <SelectItem value="recurring">Recurring</SelectItem>
                <SelectItem value="one-time">One-time</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-[200px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <p className="text-lg text-muted-foreground">No projects found</p>
              <p className="text-sm text-muted-foreground mt-2">
                Create your first project to get started
              </p>
              <Link to="/projects/new">
                <Button className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Project
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredProjects.map((project) => {
            const totalAmount =
              project.totalValue || project.totalAmount || project.paymentSchedule.reduce((sum, p) => sum + p.amount, 0);
            const paidAmount = project.paymentSchedule
              .filter((p) => p.status === 'paid')
              .reduce((sum, p) => sum + p.amount, 0);
            const progress = (paidAmount / totalAmount) * 100;
            const projectStatus = getProjectStatus(project);

            return (
              <Link key={project.id} to={`/projects/${project.id}`}>
                <Card className="card-hover h-full">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="truncate">{project.projectName}</CardTitle>
                        <CardDescription className="truncate mt-1">
                          {project.clientName}
                        </CardDescription>
                      </div>
                      <Badge variant="outline" className="capitalize whitespace-nowrap">
                        {project.paymentType === 'one-time' ? 'One-time' : project.paymentType}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Progress Bar */}
                      <div>
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-muted-foreground">Payment Progress</span>
                          <span className="font-medium">{Math.round(progress)}%</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all duration-500"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>

                      {/* Amount Info */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-muted-foreground">Received</p>
                          <p className="font-semibold">{formatCurrency(paidAmount)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Total</p>
                          <p className="font-semibold">{formatCurrency(totalAmount)}</p>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <div>
                          {projectStatus === 'overdue' && (
                            <StatusBadge status="overdue" />
                          )}
                          {projectStatus === 'active' && (
                            <Badge>Active</Badge>
                          )}
                          {projectStatus === 'completed' && (
                            <Badge className="status-paid">Completed</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Created {formatDate(project.createdAt)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};